import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatService } from '../services/chatService'
import { userService } from '../services/userService'
import { useAuth } from '../contexts/AuthContext'
import { ChatInput } from './ChatInput'
import { ChatMessage } from './ChatMessage'
import { groupMessagesByDate } from '../utils/chat'
import { Message } from '../types/chat'
import './ChatList.css'

export function ChatList() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [filter, setFilter] = useState<'team' | 'all'>('team')
  const lastCacheUpdateRef = useRef<Map<string, Message>>(new Map()) // Track last known good state per message ID

  const queryResult = useQuery({
    queryKey: ['chat-messages', filter],
    queryFn: () => filter === 'team' ? chatService.getTeamMessages() : chatService.getAll(),
    refetchInterval: 3000, // Poll every 3 seconds for new messages
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid race conditions
  })

  // Merge fetched data with ref to preserve reactions that might not be in backend yet
  const messages = useMemo(() => {
    const fetchedMessages = queryResult.data || []
    
    // Merge fetched data with ref data to preserve reactions
    const mergedMessages = fetchedMessages.map((fetchedMsg) => {
      const refMsg = lastCacheUpdateRef.current.get(fetchedMsg.id)
      // If ref has reactions, prefer them (they might be more recent from optimistic updates)
      if (refMsg?.reactions && refMsg.reactions.length > 0) {
        // Use ref reactions if fetched doesn't have them, or if ref has more
        if (!fetchedMsg.reactions || fetchedMsg.reactions.length === 0) {
          return { ...fetchedMsg, reactions: refMsg.reactions }
        }
        // If both have reactions, use the one with more (likely more up-to-date from optimistic update)
        if (refMsg.reactions.length >= fetchedMsg.reactions.length) {
          return { ...fetchedMsg, reactions: refMsg.reactions }
        }
      }
      // Update ref with fetched message if it doesn't exist in ref or has reactions
      if (!refMsg || (fetchedMsg.reactions && fetchedMsg.reactions.length > 0)) {
        lastCacheUpdateRef.current.set(fetchedMsg.id, fetchedMsg)
      }
      return fetchedMsg
    })
    
    return mergedMessages
  }, [queryResult.data, filter])
  
  const isLoading = queryResult.isLoading

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const sendMutation = useMutation({
    mutationFn: chatService.sendMessage,
    onMutate: async (newMessage) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages', filter] })

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<Message[]>(['chat-messages', filter])

      // Optimistically update to the new value
      if (previousMessages && user) {
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          content: newMessage.content,
          senderId: user.id,
          recipientId: newMessage.recipientId,
          createdAt: new Date().toISOString(),
          isTeamMessage: !newMessage.recipientId,
          readAt: undefined,
        }
        queryClient.setQueryData<Message[]>(['chat-messages', filter], (old = []) => [...old, optimisticMessage])
      }

      return { previousMessages }
    },
    onError: (_err, _newMessage, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages', filter], context.previousMessages)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  const updateMessageMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
      chatService.updateMessage(messageId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
    },
  })

  const deleteMessageMutation = useMutation({
    mutationFn: chatService.deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
    },
  })

  const addReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatService.addReaction(messageId, emoji),
    onMutate: async ({ messageId, emoji }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages', filter] })

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<Message[]>(['chat-messages', filter])

      // Optimistically update the message with the new reaction
      if (previousMessages && user) {
        const updatedMessages = previousMessages.map((msg) => {
          if (msg.id === messageId) {
            const reactions = msg.reactions || []
            // Remove existing reaction from this user for this emoji
            const filteredReactions = reactions.filter(
              (r) => !(r.userId === user.id && r.emoji === emoji)
            )
            // Add new reaction
            filteredReactions.push({
              emoji,
              userId: user.id,
              createdAt: new Date().toISOString(),
            })
            return { ...msg, reactions: filteredReactions }
          }
          return msg
        })
        queryClient.setQueryData<Message[]>(['chat-messages', filter], updatedMessages)
      }

      return { previousMessages }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages', filter], context.previousMessages)
      }
      console.error('Failed to add reaction:', error)
    },
    onSuccess: async (updatedMessage) => {
      console.log('Add reaction success, updated message:', updatedMessage, 'reactions:', updatedMessage.reactions)
      // Cancel any pending refetches to prevent overwriting our update
      await queryClient.cancelQueries({ queryKey: ['chat-messages', filter] })
      
      // Update the ref with the latest state FIRST
      lastCacheUpdateRef.current.set(updatedMessage.id, updatedMessage)
      
      // Update the cache with the server response - use the full updated message from server
      queryClient.setQueryData<Message[]>(['chat-messages', filter], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            // Use the server response which should have the latest reactions
            return updatedMessage
          }
          return msg
        })
        console.log('Updated cache for filter', filter, 'message reactions:', updated.find(m => m.id === updatedMessage.id)?.reactions)
        return updated
      })
      // Also update the other filter if it exists
      queryClient.setQueryData<Message[]>(['chat-messages', filter === 'team' ? 'all' : 'team'], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            return updatedMessage
          }
          return msg
        })
        // Update ref for other filter too
        updated.forEach((msg) => {
          lastCacheUpdateRef.current.set(msg.id, msg)
        })
        return updated
      })
      
      // Don't invalidate - we've already updated the cache with the server response
      // The refetchInterval will pick up the changes on the next cycle
    },
  })

  const removeReactionMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatService.removeReaction({ messageId, emoji }),
    onMutate: async ({ messageId, emoji }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages', filter] })

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<Message[]>(['chat-messages', filter])

      // Optimistically update the message by removing the reaction
      if (previousMessages && user) {
        const updatedMessages = previousMessages.map((msg) => {
          if (msg.id === messageId) {
            const reactions = (msg.reactions || []).filter(
              (r) => !(r.userId === user.id && r.emoji === emoji)
            )
            return { ...msg, reactions }
          }
          return msg
        })
        queryClient.setQueryData<Message[]>(['chat-messages', filter], updatedMessages)
      }

      return { previousMessages }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages', filter], context.previousMessages)
      }
      console.error('Failed to remove reaction:', error)
    },
    onSuccess: async (updatedMessage) => {
      console.log('Remove reaction success, updated message:', updatedMessage, 'reactions:', updatedMessage.reactions)
      // Cancel any pending refetches to prevent overwriting our update
      await queryClient.cancelQueries({ queryKey: ['chat-messages', filter] })
      
      // Update the ref with the latest state
      lastCacheUpdateRef.current.set(updatedMessage.id, updatedMessage)
      
      // Update the cache with the server response - use the full updated message from server
      queryClient.setQueryData<Message[]>(['chat-messages', filter], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            // Use the server response which should have the latest reactions
            return updatedMessage
          }
          return msg
        })
        console.log('Updated cache for filter', filter, 'message reactions:', updated.find(m => m.id === updatedMessage.id)?.reactions)
        return updated
      })
      // Also update the other filter if it exists
      queryClient.setQueryData<Message[]>(['chat-messages', filter === 'team' ? 'all' : 'team'], (old = []) => {
        const updated = old.map((msg) => {
          if (msg.id === updatedMessage.id) {
            return updatedMessage
          }
          return msg
        })
        // Update ref for other filter too
        updated.forEach((msg) => {
          lastCacheUpdateRef.current.set(msg.id, msg)
        })
        return updated
      })
      
      // Don't invalidate - we've already updated the cache with the server response
      // The refetchInterval will pick up the changes on the next cycle
    },
  })

  const markReadMutation = useMutation({
    mutationFn: chatService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] })
    },
  })

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mark messages as read when viewing
  useEffect(() => {
    if (messages.length > 0 && user) {
      const unreadMessages = messages.filter(
        (msg) => msg.senderId !== user.id && !msg.readAt
      )
      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map((msg) => msg.id)
        markReadMutation.mutate(messageIds)
      }
    }
  }, [messages, user])

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !user) return

    sendMutation.mutate({
      content: content.trim(),
      // For team chat, don't set recipientId
    })
  }

  const handleEditMessage = (messageId: string, newContent: string) => {
    updateMessageMutation.mutate({ messageId, content: newContent })
  }

  const handleDeleteMessage = (messageId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessageMutation.mutate(messageId)
    }
  }

  const handleAddReaction = (messageId: string, emoji: string) => {
    console.log('handleAddReaction called:', { messageId, emoji })
    addReactionMutation.mutate({ messageId, emoji })
  }

  const handleRemoveReaction = (messageId: string, emoji: string) => {
    removeReactionMutation.mutate({ messageId, emoji })
  }

  const filteredMessages = messages.filter((msg) => {
    if (filter === 'team') {
      return msg.isTeamMessage
    }
    return true
  })

  const groupedMessages = groupMessagesByDate(filteredMessages)

  if (isLoading && messages.length === 0) {
    return <div className="chat-list-loading">Loading messages...</div>
  }

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <select
          className="chat-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as 'team' | 'all')}
        >
          <option value="team">Team Chat</option>
          <option value="all">All Messages</option>
        </select>
      </div>

      <div className="chat-messages">
        {filteredMessages.length === 0 ? (
          <div className="chat-empty">No messages yet. Start the conversation!</div>
        ) : (
          groupedMessages.map((group) => (
            <div key={group.dateKey}>
              <div className="chat-date-separator">
                <span className="chat-date-separator-label">{group.dateLabel}</span>
              </div>
              {group.messages.map((message) => {
                const isOwnMessage = message.senderId === user?.id
                const sender = users.find((u) => u.id === message.senderId)
                return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    sender={sender}
                    currentUserId={user?.id}
                    isOwnMessage={isOwnMessage}
                    allUsers={users}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onAddReaction={handleAddReaction}
                    onRemoveReaction={handleRemoveReaction}
                  />
                )
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput onSend={handleSendMessage} disabled={sendMutation.isPending} />
    </div>
  )
}














